
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PageTransition from '@/components/layout/PageTransition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import DeviceStatus from '@/components/dashboard/DeviceStatus';
import { generateMacAddress } from '@/types/hierarchy';
import {
  Laptop,
  Smartphone,
  Camera,
  FileSpreadsheet,
  Router,
  Thermometer,
  Shield,
  ExternalLink,
  Plus,
  ArrowLeft,
  Wifi,
} from 'lucide-react';

// Device types enum
type DeviceType = 'laptop' | 'smartphone' | 'camera' | 'drone' | 'uuv' | 'sensor' | 'gateway';
type ApprovalStatus = 'approved' | 'pending';

// Interface for Device
interface Device {
  id: string;
  credentials: string;
  type: DeviceType;
  contactPoint: string;
  macAddress: string;
  approvalStatus: ApprovalStatus;
  status: 'online' | 'offline' | 'warning';
  addedOn: string;
  lastActive: string;
}

const deviceTypeOptions = [
  { value: 'laptop', label: 'Laptop' },
  { value: 'smartphone', label: 'Smartphone' },
  { value: 'camera', label: 'Camera' },
  { value: 'drone', label: 'Drone' },
  { value: 'uuv', label: 'UUV (Underwater Vehicle)' },
  { value: 'sensor', label: 'Sensor' },
  { value: 'gateway', label: 'Gateway' },
];

const getDeviceIcon = (type: DeviceType) => {
  switch (type) {
    case 'laptop':
      return <Laptop className="h-4 w-4" />;
    case 'smartphone':
      return <Smartphone className="h-4 w-4" />;
    case 'camera':
      return <Camera className="h-4 w-4" />;
    case 'drone':
      return <FileSpreadsheet className="h-4 w-4" />; // Using a placeholder icon
    case 'uuv':
      return <FileSpreadsheet className="h-4 w-4" />; // Using a placeholder icon
    case 'sensor':
      return <Thermometer className="h-4 w-4" />;
    case 'gateway':
      return <Router className="h-4 w-4" />;
    default:
      return <FileSpreadsheet className="h-4 w-4" />;
  }
};

const initialDevices: Device[] = [
  {
    id: 'DEV-2023-001',
    credentials: 'cred_1a2b3c4d',
    type: 'laptop',
    contactPoint: '192.168.1.101',
    macAddress: '00:1A:01:3C:4D:2B',
    approvalStatus: 'approved',
    status: 'online',
    addedOn: '2023-04-15',
    lastActive: '10 mins ago'
  },
  {
    id: 'DEV-2023-002',
    credentials: 'cred_5e6f7g8h',
    type: 'smartphone',
    contactPoint: '+1 (555) 123-4567',
    macAddress: '00:1A:02:7G:8H:3F',
    approvalStatus: 'approved',
    status: 'offline',
    addedOn: '2023-05-20',
    lastActive: '2 days ago'
  },
  {
    id: 'DEV-2023-003',
    credentials: 'cred_9i0j1k2l',
    type: 'sensor',
    contactPoint: '192.168.1.103',
    macAddress: '00:1A:03:1K:2L:9I',
    approvalStatus: 'pending',
    status: 'warning',
    addedOn: '2023-06-10',
    lastActive: '1 hour ago'
  }
];

const AddDevice = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>(initialDevices);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [deviceId, setDeviceId] = useState('');
  const [credentials, setCredentials] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType | ''>('');
  const [contactPoint, setContactPoint] = useState('');
  const [notes, setNotes] = useState('');
  const [macAddress, setMacAddress] = useState('');
  
  // Generate MAC address when device ID changes
  useEffect(() => {
    if (deviceId) {
      setMacAddress(generateMacAddress(deviceId));
    }
  }, [deviceId]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!deviceId || !credentials || !deviceType || !contactPoint) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const newDevice: Device = {
      id: deviceId,
      credentials,
      type: deviceType as DeviceType,
      contactPoint,
      macAddress: macAddress || generateMacAddress(deviceId),
      approvalStatus: 'pending', // New devices start as pending
      status: 'offline', // New devices start as offline
      addedOn: new Date().toISOString().split('T')[0],
      lastActive: 'Never'
    };
    
    setDevices([newDevice, ...devices]);
    toast.success('Device added successfully');
    resetForm();
  };
  
  const resetForm = () => {
    setDeviceId('');
    setCredentials('');
    setDeviceType('');
    setContactPoint('');
    setNotes('');
    setMacAddress('');
    setShowForm(false);
  };
  
  const generateId = () => {
    const prefix = 'DEV';
    const year = new Date().getFullYear();
    const randomNumber = Math.floor(1000 + Math.random() * 9000);
    setDeviceId(`${prefix}-${year}-${randomNumber}`);
  };

  return (
    <PageTransition>
      <div className="section-padding">
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Device Management</h1>
              <p className="text-muted-foreground">
                Add, view, and manage devices connected to your mesh network
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/devices')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Devices
              </Button>
              {!showForm && (
                <Button 
                  size="sm" 
                  onClick={() => setShowForm(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New Device
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {showForm ? (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Add New Device</CardTitle>
              <CardDescription>
                Enter the details of the device you want to add to your mesh network
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="deviceId" className="text-sm font-medium">
                      Device ID*
                    </label>
                    <div className="flex gap-2">
                      <Input
                        id="deviceId"
                        value={deviceId}
                        onChange={(e) => setDeviceId(e.target.value)}
                        placeholder="Enter device ID"
                        required
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={generateId}
                      >
                        Generate
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="credentials" className="text-sm font-medium">
                      Device Credentials*
                    </label>
                    <Input
                      id="credentials"
                      value={credentials}
                      onChange={(e) => setCredentials(e.target.value)}
                      placeholder="Enter device credentials"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="deviceType" className="text-sm font-medium">
                      Device Type*
                    </label>
                    <Select
                      value={deviceType}
                      onValueChange={(value) => setDeviceType(value as DeviceType)}
                    >
                      <SelectTrigger id="deviceType">
                        <SelectValue placeholder="Select device type" />
                      </SelectTrigger>
                      <SelectContent>
                        {deviceTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center">
                              {getDeviceIcon(option.value as DeviceType)}
                              <span className="ml-2">{option.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="contactPoint" className="text-sm font-medium">
                      Phone Number / IP Address*
                    </label>
                    <Input
                      id="contactPoint"
                      value={contactPoint}
                      onChange={(e) => setContactPoint(e.target.value)}
                      placeholder="Enter phone number or IP address"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="macAddress" className="text-sm font-medium">
                    MAC Address
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="macAddress"
                      value={macAddress}
                      onChange={(e) => setMacAddress(e.target.value)}
                      placeholder="MAC Address is auto-generated"
                      className="font-mono"
                      readOnly
                    />
                    <Wifi className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    MAC address is automatically generated from the Device ID
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="notes" className="text-sm font-medium">
                    Additional Notes
                  </label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter any additional information about this device"
                    rows={3}
                  />
                </div>
              </CardContent>
              
              <CardFooter className="flex justify-between">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Device
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}
        
        <Card>
          <CardHeader>
            <CardTitle>Recent Devices</CardTitle>
            <CardDescription>
              List of devices that have been recently added to your network
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact Point</TableHead>
                  <TableHead className="hidden md:table-cell">MAC Address</TableHead>
                  <TableHead>Approval</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added On</TableHead>
                  <TableHead className="hidden md:table-cell">Last Active</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>{device.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(device.type)}
                        <span className="capitalize">{device.type}</span>
                      </div>
                    </TableCell>
                    <TableCell>{device.contactPoint}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs">
                      {device.macAddress}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {device.approvalStatus === 'approved' ? (
                          <Shield className="h-4 w-4 text-green-500" />
                        ) : (
                          <Shield className="h-4 w-4 text-amber-500" />
                        )}
                        <span className="capitalize">{device.approvalStatus}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DeviceStatus status={device.status} />
                        <span className="capitalize">{device.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>{device.addedOn}</TableCell>
                    <TableCell className="hidden md:table-cell">{device.lastActive}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-blue-600"
                        onClick={() => toast.info('Device activity report coming soon')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Report
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  );
};

export default AddDevice;
